import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  COLS,
  ROWS,
  TILE_DEPTH,
  TILE_H,
  TILE_W,
  isoPoint,
} from './experimental/isometricGardenModel';
import { tileRenderers, setPlantsImage } from './experimental/IsometricGardenPrototype';
import grassTileImg from '@/res/tiles/isometric_0087.png';
import plantsImgUrl from '@/res/tiles/craftpix-net-200380-free-pixel-art-plants-for-farm/PNG/Plants.png';

const EARTH_DEPTH = 0;
// ── Single-tile viewport (onboarding) ──
const VIEW_COL_MIN = 7;
const VIEW_ROW_MIN = 7;
const VIEW_COL_MAX = 7;
const VIEW_ROW_MAX = 7;
const CANVAS_W = 180;
const CANVAS_H = 300;
const OFF_X = CANVAS_W / 2;
const OFF_Y = 200 - 14 * (TILE_H / 2);

// ── Full-grid viewport (dashboard) ──
const FULL_CANVAS_W = 640;
const FULL_CANVAS_H = 580;
const FULL_OFF_X = FULL_CANVAS_W / 2;
const FULL_OFF_Y = 90;

const TILE_IMG_W = TILE_W;
const TILE_IMG_H = TILE_H + TILE_DEPTH;

const SRC_X = 72;
const SRC_Y = 13;
const SRC_W = 864;
const SRC_H = 997;

const ANIM_MS = 1200;

// ── Garden growth audio ──
let gardenAudioCtx = null;
function getGardenAudio() {
  if (!gardenAudioCtx) gardenAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return gardenAudioCtx;
}

/** Phase 1: seed materialises — swooshing cutscene whoosh */
function playGardenSwish() {
  try {
    const ac = getGardenAudio();
    const now = ac.currentTime;
    const bufferSize = Math.floor(ac.sampleRate * 0.32);
    const buffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const source = ac.createBufferSource();
    source.buffer = buffer;
    const filter = ac.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2200, now);
    filter.frequency.exponentialRampToValueAtTime(350, now + 0.28);
    filter.Q.value = 1.8;
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.28, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.30);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ac.destination);
    source.start(now);
  } catch (_) {}
}

/** Phase 2: seed enters soil — earthy thud + dirt puff */
function playGardenPlant() {
  try {
    const ac = getGardenAudio();
    const now = ac.currentTime;
    const osc = ac.createOscillator();
    osc.frequency.setValueAtTime(110, now);
    osc.frequency.exponentialRampToValueAtTime(48, now + 0.16);
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.38, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(now); osc.stop(now + 0.22);
    const bufSize = Math.floor(ac.sampleRate * 0.12);
    const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const ns = ac.createBufferSource(); ns.buffer = buf;
    const nf = ac.createBiquadFilter(); nf.type = 'lowpass'; nf.frequency.value = 350;
    const ng = ac.createGain();
    ng.gain.setValueAtTime(0.22, now);
    ng.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
    ns.connect(nf); nf.connect(ng); ng.connect(ac.destination);
    ns.start(now);
  } catch (_) {}
}

/** Phase 4: sparkles — layered dopamine chime burst */
function playGardenSparkle() {
  try {
    const ac = getGardenAudio();
    const now = ac.currentTime;

    // Layer 1: satisfying sub-bass thump (reward punch)
    const sub = ac.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(90, now);
    sub.frequency.exponentialRampToValueAtTime(45, now + 0.15);
    const subG = ac.createGain();
    subG.gain.setValueAtTime(0.30, now);
    subG.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    sub.connect(subG); subG.connect(ac.destination);
    sub.start(now); sub.stop(now + 0.20);

    // Layer 2: bright ascending arpeggio (dopamine cascade)
    const arp = [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568];
    arp.forEach((freq, i) => {
      const osc = ac.createOscillator();
      osc.type = 'sine';
      const t = now + i * 0.045;
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.02, t + 0.15);
      const g = ac.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.18 - i * 0.015, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.connect(g); g.connect(ac.destination);
      osc.start(t); osc.stop(t + 0.38);
    });

    // Layer 3: high shimmer overtones (sparkle tail)
    const shimmers = [2637, 3520, 4186, 5274];
    shimmers.forEach((freq, i) => {
      const osc = ac.createOscillator();
      osc.type = 'sine';
      const t = now + 0.08 + i * 0.06;
      osc.frequency.value = freq;
      const g = ac.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.07, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.30);
      osc.connect(g); g.connect(ac.destination);
      osc.start(t); osc.stop(t + 0.32);
    });

    // Layer 4: final resolution chord (satisfying landing)
    /**const chord = [523.25, 659.25, 783.99];
    chord.forEach(freq => {
      const osc = ac.createOscillator();
      osc.type = 'sine';
      const t = now + 0.30;
      osc.frequency.value = freq;
      const g = ac.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.12, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.50);
      osc.connect(g); g.connect(ac.destination);
      osc.start(t); osc.stop(t + 0.52);
    });**/
  } catch (_) {}
}

// ── Ambient leaf particles ──
const LEAF_COUNT = 6;
const LEAF_COLORS = [
  'rgba(100,160,60,0.55)',
  'rgba(80,140,50,0.45)',
  'rgba(130,180,70,0.5)',
  'rgba(60,120,40,0.4)',
];

function createLeaf(canvasW = CANVAS_W, canvasH = CANVAS_H) {
  return {
    x: Math.random() * canvasW,
    y: -8 - Math.random() * canvasH * 0.3,
    size: 3 + Math.random() * 3,
    speedX: 0.15 + Math.random() * 0.35,
    speedY: 0.3 + Math.random() * 0.4,
    wobbleAmp: 8 + Math.random() * 12,
    wobbleFreq: 0.008 + Math.random() * 0.006,
    phase: Math.random() * Math.PI * 2,
    rotation: Math.random() * Math.PI * 2,
    rotSpeed: 0.01 + Math.random() * 0.02,
    color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
    age: 0,
  };
}

function initLeaves(canvasW = CANVAS_W, canvasH = CANVAS_H) {
  const count = canvasW > 200 ? LEAF_COUNT * 3 : LEAF_COUNT;
  const leaves = [];
  for (let i = 0; i < count; i += 1) {
    const l = createLeaf(canvasW, canvasH);
    l.y = Math.random() * canvasH;
    l.x = Math.random() * canvasW;
    leaves.push(l);
  }
  return leaves;
}

function updateLeaf(leaf, canvasW = CANVAS_W, canvasH = CANVAS_H) {
  leaf.age += 1;
  leaf.x += leaf.speedX + Math.sin(leaf.age * leaf.wobbleFreq + leaf.phase) * 0.4;
  leaf.y += leaf.speedY;
  leaf.rotation += leaf.rotSpeed;
  // Reset when off-screen
  if (leaf.y > canvasH + 10 || leaf.x > canvasW + 20) {
    leaf.x = -10 + Math.random() * canvasW * 0.6;
    leaf.y = -8 - Math.random() * 30;
    leaf.age = 0;
    leaf.phase = Math.random() * Math.PI * 2;
  }
}

function drawLeaf(ctx, leaf) {
  const { x, y, size, rotation, color, wobbleAmp, wobbleFreq, age, phase } = leaf;
  const wx = x + Math.sin(age * wobbleFreq + phase) * wobbleAmp;
  ctx.save();
  ctx.translate(wx, y);
  ctx.rotate(rotation);
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = color;
  // Simple leaf shape: two arcs
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.quadraticCurveTo(size * 1.2, 0, 0, size);
  ctx.quadraticCurveTo(-size * 1.2, 0, 0, -size);
  ctx.fill();
  ctx.restore();
}

function drawEarthWalls(ctx, offX, offY) {
  const hw = TILE_W / 2;
  const hh = TILE_H / 2;
  const bottomPt = isoPoint(COLS - 1, ROWS - 1, offX, offY);
  const bottomY = bottomPt.y + hh;
  const earthBottom = bottomY + TILE_DEPTH + EARTH_DEPTH;
  const tlPt = isoPoint(0, 0, offX, offY);
  const blPt = isoPoint(0, ROWS - 1, offX, offY);
  const trPt = isoPoint(COLS - 1, 0, offX, offY);
  const brPt = isoPoint(COLS - 1, ROWS - 1, offX, offY);
  const strataStart = bottomY + 10;

  // LEFT wall
  ctx.save();
  ctx.beginPath();
  for (let row = 0; row <= ROWS - 1; row += 1) {
    const pt = isoPoint(0, row, OFF_X, OFF_Y);
    if (row === 0) ctx.moveTo(pt.x - hw, pt.y);
    else ctx.lineTo(pt.x - hw, pt.y);
  }
  ctx.lineTo(blPt.x, blPt.y + hh);
  ctx.lineTo(blPt.x, earthBottom);
  ctx.lineTo(tlPt.x - hw, earthBottom);
  ctx.closePath();
  const leftGrad = ctx.createLinearGradient(0, tlPt.y, 0, earthBottom);
  leftGrad.addColorStop(0, '#c87832');
  leftGrad.addColorStop(0.06, '#b86828');
  leftGrad.addColorStop(0.12, '#a85e24');
  leftGrad.addColorStop(0.25, '#8B6914');
  leftGrad.addColorStop(0.45, '#7A5C12');
  leftGrad.addColorStop(0.65, '#5A3E0E');
  leftGrad.addColorStop(0.85, '#3A220A');
  leftGrad.addColorStop(1.0, '#2A1808');
  ctx.fillStyle = leftGrad;
  ctx.fill();
  ctx.globalAlpha = 0.15;
  for (let i = 0; i < 8; i += 1) {
    const sy = strataStart + i * (EARTH_DEPTH / 8);
    ctx.strokeStyle = i % 2 === 0 ? '#f0b060' : '#2e1006';
    ctx.lineWidth = i % 2 === 0 ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(tlPt.x - hw, sy);
    ctx.lineTo(blPt.x, sy);
    ctx.stroke();
  }
  ctx.restore();

  // RIGHT wall
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(trPt.x + hw, trPt.y);
  for (let row = 1; row <= ROWS - 1; row += 1) {
    const pt = isoPoint(COLS - 1, row, OFF_X, OFF_Y);
    ctx.lineTo(pt.x + hw, pt.y);
  }
  ctx.lineTo(brPt.x, brPt.y + hh);
  ctx.lineTo(brPt.x, earthBottom);
  ctx.lineTo(trPt.x + hw, earthBottom);
  ctx.closePath();
  const rightGrad = ctx.createLinearGradient(0, trPt.y, 0, earthBottom);
  rightGrad.addColorStop(0, '#a85e24');
  rightGrad.addColorStop(0.06, '#944e1c');
  rightGrad.addColorStop(0.12, '#7a3e14');
  rightGrad.addColorStop(0.25, '#6B4E10');
  rightGrad.addColorStop(0.45, '#5A3E0E');
  rightGrad.addColorStop(0.65, '#4A300C');
  rightGrad.addColorStop(0.85, '#2A1808');
  rightGrad.addColorStop(1.0, '#1A1006');
  ctx.fillStyle = rightGrad;
  ctx.fill();
  ctx.globalAlpha = 0.12;
  for (let i = 0; i < 8; i += 1) {
    const sy = strataStart + i * (EARTH_DEPTH / 8);
    ctx.strokeStyle = i % 2 === 0 ? '#d08040' : '#1e0c02';
    ctx.lineWidth = i % 2 === 0 ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(brPt.x, sy);
    ctx.lineTo(trPt.x + hw, sy);
    ctx.stroke();
  }
  ctx.restore();

  // BOTTOM face
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(blPt.x, blPt.y + hh);
  ctx.lineTo(brPt.x, brPt.y + hh);
  ctx.lineTo(brPt.x, earthBottom);
  ctx.lineTo(blPt.x, earthBottom);
  ctx.closePath();
  const bottomGrad = ctx.createLinearGradient(0, bottomY, 0, earthBottom);
  bottomGrad.addColorStop(0, '#6B4E10');
  bottomGrad.addColorStop(0.3, '#5A3E0E');
  bottomGrad.addColorStop(0.7, '#3A220A');
  bottomGrad.addColorStop(1.0, '#2A1808');
  ctx.fillStyle = bottomGrad;
  ctx.fill();
  ctx.restore();
}

// ── Completion animation overlay (1.2 s reward loop) ──
// 4 phases: seed drop → soil glow → plant grow → sparkle
function drawCompletionOverlay(ctx, tile, progress, offX, offY) {
  const p = Math.max(0, Math.min(1, progress));
  const [row = 0, col = 0] = tile || [];
  const pt = isoPoint(col, row, offX, offY);
  const cx = pt.x;
  const cy = pt.y + TILE_H * 0.15;

  // Phase 1 – big floating seed shrinks as it falls fast (0 → 0.25)
  if (p < 0.25) {
    const t = p / 0.25;
    const ease = t * t * t;           // cubic ease-in — accelerates
    const sy = cy - 60 + 66 * ease;   // starts 60px above, lands at cy+6
    const size = 14 - 10 * t;         // shrinks from 14 → 4
    const innerSize = size * 0.6;
    const rotation = t * Math.PI * 1.5;
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.translate(cx, sy);
    ctx.rotate(rotation);
    // outer seed
    ctx.fillStyle = '#8B6914';
    ctx.beginPath();
    ctx.ellipse(0, 0, size * 0.8, size, 0, 0, Math.PI * 2);
    ctx.fill();
    // inner highlight
    ctx.fillStyle = '#c8a040';
    ctx.beginPath();
    ctx.ellipse(-size * 0.1, -size * 0.15, innerSize * 0.7, innerSize, 0, 0, Math.PI * 2);
    ctx.fill();
    // seed tip
    ctx.fillStyle = '#6B4E10';
    ctx.beginPath();
    ctx.ellipse(0, size * 0.8, size * 0.25, size * 0.35, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Phase 2 – soil glow (0.2 → 0.5)
  if (p >= 0.2 && p < 0.5) {
    const t = (p - 0.2) / 0.3;
    const alpha = 0.45 * Math.sin(t * Math.PI);
    const r = 28 + 14 * t;
    ctx.save();
    const g = ctx.createRadialGradient(cx, cy + 6, 0, cx, cy + 6, r);
    g.addColorStop(0, `rgba(255,240,170,${alpha})`);
    g.addColorStop(1, 'rgba(255,240,170,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy + 6, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Phase 3 – plant grow ring (0.45 → 0.75)
  if (p >= 0.45 && p < 0.75) {
    const t = (p - 0.45) / 0.3;
    const alpha = 0.4 * Math.sin(t * Math.PI);
    const r = 18 + 20 * t;
    ctx.save();
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, `rgba(180,255,180,${alpha})`);
    g.addColorStop(0.6, `rgba(120,230,120,${alpha * 0.5})`);
    g.addColorStop(1, 'rgba(100,200,140,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Phase 4 – sparkles (0.7 → 1.0) — rich burst with golden glow
  if (p >= 0.7) {
    const t = (p - 0.7) / 0.3;
    const fade = 1 - t;

    // Central golden flash (dopamine burst)
    if (t < 0.35) {
      const flashAlpha = 0.5 * (1 - t / 0.35);
      const flashR = 24 + 20 * (t / 0.35);
      ctx.save();
      const fg = ctx.createRadialGradient(cx, cy, 0, cx, cy, flashR);
      fg.addColorStop(0, `rgba(255,230,120,${flashAlpha})`);
      fg.addColorStop(0.5, `rgba(255,200,60,${flashAlpha * 0.5})`);
      fg.addColorStop(1, 'rgba(255,200,60,0)');
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.arc(cx, cy, flashR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    // Primary ring — 12 sparks, mixed gold/white
    const n = 12;
    for (let i = 0; i < n; i += 1) {
      const a = (Math.PI * 2 * i) / n + t * Math.PI * 1.5;
      const dist = 14 + 24 * t;
      const sx = cx + Math.cos(a) * dist;
      const sy = cy + Math.sin(a) * dist * 0.6 - 6 * t;
      const sz = (2.5 + 3 * fade) * (0.7 + 0.3 * Math.sin(i * 1.7 + t * 12));
      const isGold = i % 3 === 0;
      ctx.globalAlpha = (isGold ? 0.95 : 0.8) * fade;
      ctx.fillStyle = isGold ? `rgba(255,215,0,${0.95 * fade})` : `rgba(255,255,230,${0.85 * fade})`;
      ctx.beginPath();
      ctx.arc(sx, sy, sz, 0, Math.PI * 2);
      ctx.fill();
    }
    // Secondary ring — 6 smaller trailing sparks
    const m = 6;
    for (let i = 0; i < m; i += 1) {
      const a = (Math.PI * 2 * i) / m - t * Math.PI * 0.8;
      const dist2 = 10 + 32 * t;
      const sx = cx + Math.cos(a) * dist2;
      const sy = cy + Math.sin(a) * dist2 * 0.5 - 8 * t;
      const sz = 1.5 + 2 * fade;
      ctx.globalAlpha = 0.6 * fade;
      ctx.fillStyle = `rgba(255,240,180,${0.7 * fade})`;
      ctx.beginPath();
      ctx.arc(sx, sy, sz, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

function IsometricGarden({ grid, completionAnimation, fullView = false }) {
  const cw = fullView ? FULL_CANVAS_W : CANVAS_W;
  const ch = fullView ? FULL_CANVAS_H : CANVAS_H;
  const ox = fullView ? FULL_OFF_X : OFF_X;
  const oy = fullView ? FULL_OFF_Y : OFF_Y;
  const colMin = fullView ? 0 : VIEW_COL_MIN;
  const colMax = fullView ? COLS - 1 : VIEW_COL_MAX;
  const rowMin = fullView ? 0 : VIEW_ROW_MIN;
  const rowMax = fullView ? ROWS - 1 : VIEW_ROW_MAX;

  const canvasRef = useRef(null);
  const [version, setVersion] = useState(0);
  const grassImgRef = useRef(null);
  const drawGridRef = useRef(null);
  const ambientRafRef = useRef(null);
  const leavesRef = useRef(null);
  const completionRef = useRef(null);
  const zoomWrapperRef = useRef(null);
  const zoomTimerRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.src = grassTileImg;
    img.onload = () => {
      grassImgRef.current = img;
      setVersion((v) => v + 1);
    };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = plantsImgUrl;
    img.onload = () => {
      setPlantsImage(img);
      setVersion((v) => v + 1);
    };
  }, []);

  const drawGrid = useCallback((ctx) => {
    ctx.clearRect(0, 0, cw, ch);
    // Pass 1: draw all grass tiles first
    for (let row = rowMin; row <= rowMax; row += 1) {
      for (let col = colMin; col <= colMax; col += 1) {
        const point = isoPoint(col, row, ox, oy);
        const x = point.x - TILE_W / 2;
        const y = point.y;
        if (grassImgRef.current) {
          ctx.drawImage(
            grassImgRef.current,
            SRC_X, SRC_Y, SRC_W, SRC_H,
            x, y - TILE_H / 2,
            TILE_IMG_W, TILE_IMG_H,
          );
        }
      }
    }
    // Pass 2: draw all plants/trees on top so they are never clipped by grass
    for (let row = rowMin; row <= rowMax; row += 1) {
      for (let col = colMin; col <= colMax; col += 1) {
        const point = isoPoint(col, row, ox, oy);
        const x = point.x - TILE_W / 2;
        const y = point.y;
        const tileType = grid[row][col];
        if (tileType !== 'grass') {
          tileRenderers[tileType]?.(ctx, x, y);
        }
      }
    }
  }, [grid, cw, ch, ox, oy, colMin, colMax, rowMin, rowMax]);

  // Keep drawGrid ref current for animation loop
  useEffect(() => { drawGridRef.current = drawGrid; }, [drawGrid]);

  // Static redraw (only when no ambient loop is running)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let ctx;
    try { ctx = canvas.getContext('2d'); } catch (_) { return; }
    if (!ctx) return;
    drawGrid(ctx);
  }, [drawGrid, version]);

  useEffect(() => {
    setVersion((v) => v + 1);
  }, [grid]);

  // ── Ambient leaf animation (always running) + completion overlay ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let ctx;
    try { ctx = canvas.getContext('2d'); } catch (_) { return; }
    if (!ctx) return;

    // Re-create leaves for current canvas size
    leavesRef.current = initLeaves(cw, ch);
    const leaves = leavesRef.current;

    const tick = (now) => {
      (drawGridRef.current || drawGrid)(ctx);

      // Completion overlay (if active)
      const ca = completionRef.current;
      if (ca) {
        const elapsed = now - ca.start;
        const progress = Math.min(1, elapsed / ANIM_MS);
        drawCompletionOverlay(ctx, ca.tile, progress, ox, oy);
        if (progress >= 1) completionRef.current = null;
      }

      // Drifting leaves on top
      for (const leaf of leaves) {
        updateLeaf(leaf, cw, ch);
        drawLeaf(ctx, leaf);
      }
      ambientRafRef.current = requestAnimationFrame(tick);
    };
    ambientRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (ambientRafRef.current) {
        cancelAnimationFrame(ambientRafRef.current);
        ambientRafRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullView]);

  // Deduplicate: track the last animation we played so navigating back doesn't replay it
  const lastAnimIdRef = useRef(null);

  // Sync completionAnimation prop into ref for ambient loop + trigger sounds
  useEffect(() => {
    if (completionAnimation?.tile) {
      const animId = `${completionAnimation.tile.join(',')}@${completionAnimation.start}`;
      if (animId === lastAnimIdRef.current) return; // already played this one
      lastAnimIdRef.current = animId;

      completionRef.current = {
        tile: completionAnimation.tile,
        start: completionAnimation.start || performance.now(),
      };
      // Sound 1: swish as seed materialises
      playGardenSwish();
      // Sound 2: earthy plop as seed enters soil (phase 2 starts ~22%)
      const t2 = setTimeout(playGardenPlant, ANIM_MS * 0.22);
      // Sound 3: sparkle chime as sparkles burst (phase 4 starts ~72%)
      const t3 = setTimeout(playGardenSparkle, ANIM_MS * 0.72);
      return () => { clearTimeout(t2); clearTimeout(t3); };
    }
  }, [completionAnimation]);

  // ── Zoom-in on the growing tile, then zoom back out ──
  const lastZoomIdRef = useRef(null);
  useEffect(() => {
    if (!completionAnimation?.tile || !fullView) return;
    const zoomId = `${completionAnimation.tile.join(',')}@${completionAnimation.start}`;
    if (zoomId === lastZoomIdRef.current) return; // already zoomed for this one
    lastZoomIdRef.current = zoomId;

    const wrapper = zoomWrapperRef.current;
    if (!wrapper) return;

    const [tileRow, tileCol] = completionAnimation.tile;
    const pt = isoPoint(tileCol, tileRow, FULL_OFF_X, FULL_OFF_Y);
    const oxPct = Math.max(10, Math.min(90, (pt.x / FULL_CANVAS_W) * 100));
    const oyPct = Math.max(10, Math.min(90, (pt.y / FULL_CANVAS_H) * 100));

    // Set origin instantly while at scale(1) — no visual jump
    wrapper.style.transition = 'none';
    wrapper.style.transformOrigin = `${oxPct}% ${oyPct}%`;
    wrapper.style.transform = 'scale(1)';

    // Next frame: animate zoom-in
    const rafId = requestAnimationFrame(() => {
      wrapper.style.transition = 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      wrapper.style.transform = 'scale(2.2)';
    });

    // After animation + hold, zoom back out
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      wrapper.style.transition = 'transform 0.75s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      wrapper.style.transform = 'scale(1)';
    }, ANIM_MS + 500);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(zoomTimerRef.current);
    };
  }, [completionAnimation, fullView]);

  return (
    <div ref={zoomWrapperRef} style={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        width={cw}
        height={ch}
        data-testid="garden-canvas"
        style={{ width: '100%', maxWidth: '100%', display: 'block', transform: 'scale(0.8)', overflow: 'hidden' }}
      />
    </div>
  );
}

export default IsometricGarden;
